import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import HomeHeader from '../components/HomeHeader';
import {
  IconSearch, IconDashboard, IconDatabase, IconHistory,
  IconShield, IconGlobe, IconFlag, IconAlert, IconBot,
} from '../components/Icons';

// ============================================================
// SUPPLY CHAIN · ANALYZE — intelligence launcher (/supplychain)
// The former TradeSpy nav bar becomes app tiles on a dashboard:
// query box -> suggestion chips -> favorites -> tile sections.
// TradeSpy identity is retained via red accent, wordmark/case-file chrome,
// the alert ticker (rendered by the app shell) and the two floating agents.
// ============================================================

interface HomeUser {
  email: string;
  is_admin: boolean;
}

interface HomeLauncherProps {
  user: HomeUser;
  onLogout: () => void;
  totalProducts: number;
  currentShortages: number;
  onSubmitQuery: (q: string) => void;
}

const SERIF = "'Playfair Display', Georgia, serif";
const MONO = "'IBM Plex Mono', 'Courier New', monospace";
const SANS = "'Inter', -apple-system, sans-serif";

const AGENT_COUNT = '30+';

interface TileDef {
  id: string;
  title: string;
  caption: string;
  desc: string;
  path: string;
  icon: React.ReactNode;
}

const PaperclipIcon = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

export default function HomeLauncher({
  user,
  onLogout,
  totalProducts,
  currentShortages,
  onSubmitQuery,
}: HomeLauncherProps) {
  const { T } = useTheme();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');

  const nf = new Intl.NumberFormat('en-US');
  const ndcCount = totalProducts > 0 ? nf.format(totalProducts) : '149,000+';
  const shortageCount = currentShortages > 0 ? nf.format(currentShortages) : '929';

  const submit = (q: string) => {
    const text = q.trim();
    if (!text) return;
    onSubmitQuery(text);
  };

  const suggestionChips = [
    'Show me CCP-affiliated manufacturers',
    'Country of origin for a given NDC',
    'Active shortages in the last 7 days',
    'Run a supply-chain risk analysis',
    'Generate an intelligence briefing',
  ];

  const sections: { label: string; tiles: TileDef[] }[] = [
    {
      label: '// INTELLIGENCE',
      tiles: [
        { id: 'dashboard', title: 'Dashboard', caption: 'IARI · LIVE', desc: 'Command-center overview of supply-chain risk and compliance.', path: '/dashboard', icon: <IconDashboard /> },
        { id: 'risk-analyzer', title: 'Risk Analyzer', caption: 'RUN ANALYSIS', desc: 'Model supply-chain exposure and stress-test scenarios.', path: '/risk-analyzer', icon: <IconAlert /> },
        { id: 'briefings', title: 'Briefings', caption: 'INTEL DIGEST', desc: 'AI-generated intelligence briefings on demand.', path: '/briefings', icon: <IconHistory /> },
        { id: 'query', title: 'Query', caption: `${ndcCount} NDCs`, desc: 'Ask natural-language questions across the database.', path: '/search', icon: <IconSearch /> },
      ],
    },
    {
      label: '// SUPPLY CHAIN',
      tiles: [
        { id: 'products', title: 'Drugs', caption: `${ndcCount} NDCs`, desc: 'Searchable database of scored pharmaceutical products.', path: '/products', icon: <IconDatabase /> },
        { id: 'apis', title: "API's", caption: 'SOURCING', desc: 'Active pharmaceutical ingredient origin intelligence.', path: '/apis', icon: <IconGlobe /> },
        { id: 'manufacturers', title: 'Manufacturers', caption: 'PROFILES', desc: 'Manufacturer profiles, ownership, and CCP exposure.', path: '/manufacturers', icon: <IconShield /> },
        { id: 'shortage-warning', title: 'Shortages', caption: `${shortageCount} ACTIVE`, desc: 'FDA-reported drug shortages and early warnings.', path: '/shortage-warning', icon: <IconAlert /> },
      ],
    },
    {
      label: '// COMPLIANCE & GLOBAL',
      tiles: [
        { id: 'china-dependency', title: 'CCP', caption: 'HARD FLOOR', desc: 'Chinese Communist Party supply-chain exposure mapping.', path: '/china-dependency', icon: <IconFlag /> },
        { id: 'geopolitical', title: 'Global', caption: 'GEOPOLITICAL', desc: 'Country-of-origin and geopolitical risk intelligence.', path: '/geopolitical', icon: <IconGlobe /> },
        { id: 'agent-activity', title: 'Agents', caption: `${AGENT_COUNT} ONLINE`, desc: 'Autonomous data-steward and scoring agent activity.', path: '/agent-activity', icon: <IconBot /> },
      ],
    },
  ];

  const favorites: TileDef[] = [
    sections[0].tiles[0], // Dashboard
    sections[0].tiles[1], // Risk Analyzer
  ];

  // ---- shared tile renderer ----
  const renderTile = (tile: TileDef, key: string) => (
    <button
      key={key}
      className="hl-tile"
      onClick={() => navigate(tile.path)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10,
        textAlign: 'left', cursor: 'pointer',
        background: T.surfaceCard, border: `1px solid ${T.borderCard}`,
        borderRadius: T.radius, boxShadow: T.shadowRest,
        padding: '18px 18px 20px',
        transition: 'transform .16s ease, box-shadow .16s ease',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 38, height: 38, borderRadius: 10,
        background: 'rgba(200,22,10,0.08)', color: T.red,
      }}>
        {tile.icon}
      </div>
      <div style={{ fontFamily: SERIF, fontSize: 19, fontWeight: 700, color: T.ink, lineHeight: 1.15 }}>
        {tile.title}
      </div>
      <div style={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', color: T.red, textTransform: 'uppercase' }}>
        {tile.caption}
      </div>
      <div style={{ fontFamily: SANS, fontSize: 13, color: T.textMid, lineHeight: 1.5 }}>
        {tile.desc}
      </div>
    </button>
  );

  return (
    <div style={{ background: T.surfacePage, minHeight: '100%' }}>
      <style>{`
        .hl-tile:hover { box-shadow: ${T.shadowHover}; transform: translateY(-3px); }
        .hl-chip:hover { border-color: rgba(200,22,10,0.45); color: ${T.red}; box-shadow: ${T.shadowRest}; }
        .hl-send:hover { filter: brightness(1.08); }
        .hl-breadcrumb-link:hover { text-decoration: underline; }
        @media (prefers-reduced-motion: reduce) {
          .hl-tile:hover { transform: none; }
        }
      `}</style>

      <HomeHeader user={user} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={{
        maxWidth: 1120, margin: '14px auto 0',
        padding: isMobile ? '0 20px' : '0 40px',
        fontFamily: MONO, fontSize: 10.5, letterSpacing: '0.14em',
      }}>
        <Link to="/" className="hl-breadcrumb-link" style={{ color: T.red, textDecoration: 'none' }}>← HOME</Link>
        <span style={{ color: T.textLight }}> / </span>
        <span style={{ color: T.ink }}>SUPPLY CHAIN · ANALYZE</span>
      </div>

      {/* Body */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: isMobile ? '28px 20px 56px' : '44px 40px 72px' }}>

        {/* Decorative mark */}
        <div style={{ width: 34, height: 4, borderRadius: 2, background: T.red, margin: '0 auto 22px' }} />

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <h1 style={{
            fontFamily: SERIF, fontWeight: 700,
            fontSize: isMobile ? 30 : 40, color: T.ink,
            margin: 0, letterSpacing: '-0.01em',
          }}>
            Supply Chain — Analyze
          </h1>
          <div style={{
            fontFamily: MONO, fontSize: 11.5, letterSpacing: '0.14em',
            color: T.textMid, textTransform: 'uppercase', marginTop: 12,
          }}>
            Active Surveillance · {ndcCount} NDCs Monitored
          </div>
        </div>

        {/* Query box */}
        <form
          onSubmit={(e) => { e.preventDefault(); submit(query); }}
          style={{
            background: T.surfaceChat, borderRadius: 16, boxShadow: T.shadowChat,
            border: `1px solid ${T.borderCard}`,
            padding: isMobile ? '16px' : '18px 20px',
            maxWidth: 760, margin: '0 auto',
          }}
        >
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(query); } }}
            placeholder="Ask the Director or a Senior Analyst about supply-chain intelligence…"
            rows={2}
            style={{
              width: '100%', border: 'none', outline: 'none', resize: 'none',
              background: 'transparent', color: T.ink,
              fontFamily: SANS, fontSize: 15, lineHeight: 1.5,
              minHeight: 46,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
            <span style={{ color: T.textLight, display: 'flex' }}><PaperclipIcon color={T.textLight} /></span>
            <button
              type="submit"
              className="hl-send"
              aria-label="Send"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 40, height: 40, borderRadius: 10, border: 'none',
                background: T.red, cursor: 'pointer',
              }}
            >
              <SendIcon />
            </button>
          </div>
        </form>

        {/* Suggestion chips */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10,
          maxWidth: 760, margin: '18px auto 0',
        }}>
          {suggestionChips.map(chip => (
            <button
              key={chip}
              className="hl-chip"
              onClick={() => submit(chip)}
              style={{
                background: T.surfaceCard, border: `1px solid ${T.borderCard}`,
                borderRadius: 999, padding: '9px 16px', cursor: 'pointer',
                fontFamily: MONO, fontSize: 12, color: T.ink,
                transition: 'border-color .15s ease, color .15s ease, box-shadow .15s ease',
              }}
            >
              {chip}
            </button>
          ))}
        </div>

        {/* Favorites band */}
        <div style={{
          background: T.favoritesBg, border: `1px solid rgba(200,22,10,0.18)`,
          borderRadius: T.radius, padding: isMobile ? '16px' : '18px 20px',
          margin: '40px 0 32px',
        }}>
          <div style={{
            fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: '0.14em',
            color: T.red, textTransform: 'uppercase', marginBottom: 14,
          }}>
            ★ Favorites
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 16,
          }}>
            {favorites.map((t, i) => renderTile(t, `fav-${i}`))}
          </div>
        </div>

        {/* App-tile sections */}
        {sections.map(section => (
          <div key={section.label} style={{ marginBottom: 34 }}>
            <div style={{
              fontFamily: MONO, fontSize: 11, fontWeight: 600, letterSpacing: '0.14em',
              color: T.textMid, textTransform: 'uppercase', marginBottom: 14,
            }}>
              {section.label}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
              gap: 16,
            }}>
              {section.tiles.map(t => renderTile(t, t.id))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
